import React from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  Alert,
  AlertTitle,
  Container
} from '@mui/material';
import {
  Error as ErrorIcon,
  Refresh as RefreshIcon,
  Home as HomeIcon,
  Report as ReportIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { 
      hasError: false, 
      error: null, 
      errorInfo: null,
      errorId: null
    };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    // Generate a unique error ID for tracking
    const errorId = `ERR_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    
    this.setState({
      error,
      errorInfo,
      errorId
    });

    // Log error to console for debugging
    console.error('Error caught by boundary:', {
      error,
      errorInfo,
      errorId,
      timestamp: new Date().toISOString(),
      userAgent: navigator.userAgent,
      url: window.location.href
    });

    // In production, you could send this to an error tracking service
    // this.logErrorToService(error, errorInfo, errorId);
  }

  logErrorToService = (error, errorInfo, errorId) => {
    // Example: Send error to external service
    try {
      fetch('/api/errors', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          errorId,
          message: error.message,
          stack: error.stack,
          componentStack: errorInfo.componentStack,
          timestamp: new Date().toISOString(),
          url: window.location.href,
          userAgent: navigator.userAgent
        })
      });
    } catch (logError) {
      console.error('Failed to log error to service:', logError);
    }
  };

  handleRefresh = () => {
    window.location.reload();
  };

  handleGoHome = () => {
    this.setState({ hasError: false });
    window.location.href = '/';
  };

  render() {
    if (this.state.hasError) {
      return (
        <ErrorFallback
          error={this.state.error}
          errorInfo={this.state.errorInfo}
          errorId={this.state.errorId}
          onRefresh={this.handleRefresh}
          onGoHome={this.handleGoHome}
        />
      );
    }

    return this.props.children;
  }
}

const ErrorFallback = ({ error, errorInfo, errorId, onRefresh, onGoHome }) => {
  const navigate = useNavigate();

  return (
    <Container maxWidth="md" sx={{ py: 8 }}>
      <Paper 
        elevation={3} 
        sx={{ 
          p: 4, 
          textAlign: 'center',
          borderRadius: 3,
          background: 'linear-gradient(135deg, #fff5f5 0%, #fef2f2 100%)',
          border: '1px solid #fecaca'
        }}
      >
        <Box sx={{ mb: 4 }}>
          <ErrorIcon sx={{ fontSize: 80, color: 'error.main', mb: 2 }} />
          <Typography variant="h3" component="h1" gutterBottom color="error.main" sx={{ fontWeight: 700 }}>
            Oops! Something went wrong
          </Typography>
          <Typography variant="h6" color="text.secondary" gutterBottom>
            We encountered an unexpected error
          </Typography>
        </Box>

        <Alert severity="error" sx={{ mb: 3, textAlign: 'left' }}>
          <AlertTitle>Error Details</AlertTitle>
          <Typography variant="body2" component="div">
            <strong>Error ID:</strong> {errorId}
          </Typography>
          {error && (
            <Typography variant="body2" component="div" sx={{ mt: 1 }}>
              <strong>Message:</strong> {error.message}
            </Typography>
          )}
          {process.env.NODE_ENV === 'development' && errorInfo && (
            <Typography variant="body2" component="div" sx={{ mt: 1 }}>
              <strong>Component Stack:</strong>
              <Box component="pre" sx={{ 
                mt: 1, 
                p: 2, 
                bgcolor: 'grey.100', 
                borderRadius: 1, 
                fontSize: '0.75rem',
                overflow: 'auto',
                maxHeight: 200
              }}>
                {errorInfo.componentStack}
              </Box>
            </Typography>
          )}
        </Alert>

        <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
          <Button
            variant="contained"
            startIcon={<RefreshIcon />}
            onClick={onRefresh}
            sx={{ 
              px: 3, 
              py: 1.5,
              borderRadius: 2,
              fontWeight: 600
            }}
          >
            Refresh Page
          </Button>
          
          <Button
            variant="outlined"
            startIcon={<HomeIcon />}
            onClick={() => navigate('/')}
            sx={{ 
              px: 3, 
              py: 1.5,
              borderRadius: 2,
              fontWeight: 600
            }}
          >
            Go to Dashboard
          </Button>

          <Button
            variant="text"
            startIcon={<ReportIcon />}
            onClick={() => {
              // Copy error details to clipboard
              const errorDetails = `Error ID: ${errorId}\nMessage: ${error?.message}\nURL: ${window.location.href}\nTime: ${new Date().toISOString()}`;
              navigator.clipboard.writeText(errorDetails);
              alert('Error details copied to clipboard');
            }}
            sx={{ 
              px: 3, 
              py: 1.5,
              borderRadius: 2,
              fontWeight: 600
            }}
          >
            Copy Error Details
          </Button>
        </Box>

        <Box sx={{ mt: 4, p: 3, bgcolor: 'grey.50', borderRadius: 2 }}>
          <Typography variant="body2" color="text.secondary">
            If this problem persists, please contact support with the Error ID above.
          </Typography>
        </Box>
      </Paper>
    </Container>
  );
};

export default ErrorBoundary;
